import React, { useState, useEffect } from 'react';
import { cpdAdminAPI, resourcesAPI, emailListsAPI } from '../../services/api';

const KNOWLEDGE_AREAS = [
  { value: 'technical', label: 'Technical Competence' },
  { value: 'client_care', label: 'Client Care & Practice' },
  { value: 'regulatory', label: 'Regulatory Compliance' },
  { value: 'professionalism', label: 'Professionalism & Ethics' },
];

const CpdEditor = () => {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [resourceItems, setResourceItems] = useState([]);

  // Task form
  const [formData, setFormData] = useState({
    faa_number: '',
    title: '',
    description: '',
    task_type: 'video',
    resource_item_id: '',
    pass_mark: 80,
    cpd_hours: 1.0,
    knowledge_area: 'technical',
  });

  // File uploads
  const [certificateFile, setCertificateFile] = useState(null);
  const [caseStudyFile, setCaseStudyFile] = useState(null);

  // Question management
  const [showQuestions, setShowQuestions] = useState(null); // task id
  const [questions, setQuestions] = useState([]);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    options: ['', '', '', ''],
    correct_option: 0,
  });
  const [editingQuestion, setEditingQuestion] = useState(null);

  // Completions view
  const [showCompletions, setShowCompletions] = useState(null);
  const [completions, setCompletions] = useState([]);

  // Compliance report
  const [showCompliance, setShowCompliance] = useState(false);
  const [complianceYear, setComplianceYear] = useState(new Date().getFullYear());
  const [complianceData, setComplianceData] = useState([]);
  const [complianceLoading, setComplianceLoading] = useState(false);

  // Quiz notification settings
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifConfig, setNotifConfig] = useState({ notify_on_attempt: false, notify_on_pass: false, email_list_ids: [] });
  const [emailLists, setEmailLists] = useState([]);
  const [notifSaving, setNotifSaving] = useState(false);

  useEffect(() => {
    fetchTasks();
    fetchResources();
  }, []);

  const fetchTasks = async () => {
    setIsLoading(true);
    try {
      const data = await cpdAdminAPI.getTasks();
      setTasks(data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load CPD tasks' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      const items = await resourcesAPI.getCpdEligible();
      setResourceItems(items);
    } catch {
      // Non-critical
    }
  };

  const resetForm = () => {
    setFormData({ faa_number: '', title: '', description: '', task_type: 'video', resource_item_id: '', pass_mark: 80, cpd_hours: 1.0, knowledge_area: 'technical' });
    setCertificateFile(null);
    setCaseStudyFile(null);
    setEditingTask(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    try {
      const payload = {
        ...formData,
        resource_item_id: formData.resource_item_id ? parseInt(formData.resource_item_id) : null,
        pass_mark: formData.task_type === 'quiz' ? parseInt(formData.pass_mark) : null,
        cpd_hours: parseFloat(formData.cpd_hours) || 1.0,
      };

      let task;
      if (editingTask) {
        const { faa_number, task_type, ...updateData } = payload;
        task = await cpdAdminAPI.updateTask(editingTask.id, updateData);
      } else {
        task = await cpdAdminAPI.createTask(payload);
      }

      // Upload files if selected
      const taskId = task.id || editingTask?.id;
      if (certificateFile && taskId) {
        await cpdAdminAPI.uploadCertificate(taskId, certificateFile);
      }
      if (caseStudyFile && taskId) {
        await cpdAdminAPI.uploadCaseStudy(taskId, caseStudyFile);
      }

      setMessage({ type: 'success', text: editingTask ? 'Task updated' : 'Task created' });
      resetForm();
      fetchTasks();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save task' });
    }
  };

  const handleEdit = (task) => {
    setFormData({
      faa_number: task.faa_number,
      title: task.title,
      description: task.description || '',
      task_type: task.task_type,
      resource_item_id: task.resource_item_id || '',
      pass_mark: task.pass_mark || 80,
      cpd_hours: task.cpd_hours ?? 1.0,
      knowledge_area: task.knowledge_area || 'technical',
    });
    setEditingTask(task);
    setShowForm(true);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this CPD task? This will also delete all questions and completion records.')) return;
    try {
      await cpdAdminAPI.deleteTask(taskId);
      setMessage({ type: 'success', text: 'Task deleted' });
      fetchTasks();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete' });
    }
  };

  // ─── Questions ────────────────────────────────────────────────

  const openQuestions = async (taskId) => {
    setShowQuestions(taskId);
    try {
      const data = await cpdAdminAPI.getQuestions(taskId);
      setQuestions(data);
    } catch {
      setQuestions([]);
    }
  };

  const resetQuestionForm = () => {
    setQuestionForm({ question_text: '', options: ['', '', '', ''], correct_option: 0 });
    setEditingQuestion(null);
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    const data = {
      question_text: questionForm.question_text,
      options: questionForm.options.filter(o => o.trim() !== ''),
      correct_option: questionForm.correct_option,
    };
    if (data.options.length < 2) {
      setMessage({ type: 'error', text: 'At least 2 options required' });
      return;
    }
    try {
      if (editingQuestion) {
        await cpdAdminAPI.updateQuestion(editingQuestion.id, data);
      } else {
        await cpdAdminAPI.createQuestion(showQuestions, data);
      }
      resetQuestionForm();
      openQuestions(showQuestions);
      fetchTasks();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save question' });
    }
  };

  const handleEditQuestion = (q) => {
    const opts = [...q.options];
    while (opts.length < 4) opts.push('');
    setQuestionForm({ question_text: q.question_text, options: opts, correct_option: q.correct_option });
    setEditingQuestion(q);
  };

  const handleDeleteQuestion = async (qId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await cpdAdminAPI.deleteQuestion(qId);
      openQuestions(showQuestions);
      fetchTasks();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete question' });
    }
  };

  // ─── Completions ──────────────────────────────────────────────

  const openCompletions = async (taskId) => {
    setShowCompletions(taskId);
    try {
      const data = await cpdAdminAPI.getCompletions(taskId);
      setCompletions(data);
    } catch {
      setCompletions([]);
    }
  };

  // ─── Compliance Report ────────────────────────────────────────

  const fetchComplianceReport = async (year) => {
    setComplianceLoading(true);
    try {
      const data = await cpdAdminAPI.getComplianceReport(year);
      setComplianceData(data);
    } catch {
      setComplianceData([]);
      setMessage({ type: 'error', text: 'Failed to load compliance report' });
    } finally {
      setComplianceLoading(false);
    }
  };

  const handleShowCompliance = () => {
    setShowCompliance(true);
    fetchComplianceReport(complianceYear);
  };

  const handleExportCSV = async () => {
    try {
      const response = await cpdAdminAPI.exportComplianceCSV(complianceYear);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cpd_compliance_${complianceYear}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setMessage({ type: 'error', text: 'Failed to export CSV' });
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 3; y--) yearOptions.push(y);

  // ─── Render ───────────────────────────────────────────────────

  const handleShowNotifications = async () => {
    try {
      const [config, lists] = await Promise.all([
        cpdAdminAPI.getNotificationConfig(),
        emailListsAPI.getLists(),
      ]);
      setNotifConfig({
        notify_on_attempt: !!config.notify_on_attempt,
        notify_on_pass: !!config.notify_on_pass,
        email_list_ids: config.email_list_ids || [],
      });
      setEmailLists((lists || []).filter(l => l.is_active));
      setShowNotifications(true);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load notification settings' });
    }
  };

  const toggleNotifList = (listId) => {
    setNotifConfig(prev => ({
      ...prev,
      email_list_ids: prev.email_list_ids.includes(listId)
        ? prev.email_list_ids.filter(id => id !== listId)
        : [...prev.email_list_ids, listId],
    }));
  };

  const saveNotifConfig = async () => {
    setNotifSaving(true);
    try {
      await cpdAdminAPI.updateNotificationConfig(notifConfig);
      setMessage({ type: 'success', text: 'Notification settings saved' });
      setShowNotifications(false);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save notification settings' });
    } finally {
      setNotifSaving(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">CPD Management</h4>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-primary" onClick={handleShowNotifications}>
            Notifications
          </button>
          <button className="btn btn-sm btn-outline-primary" onClick={handleShowCompliance}>
            Compliance Report
          </button>
          <button className="btn btn-sm text-white" style={{ backgroundColor: '#072040' }} onClick={() => { resetForm(); setShowForm(!showForm); }}>
            {showForm ? 'Cancel' : '+ New Task'}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)} />
        </div>
      )}

      {/* ─── Quiz Notification Settings ─── */}
      {showNotifications && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Quiz Notification Emails</h5>
              <button type="button" className="btn-close" onClick={() => setShowNotifications(false)} />
            </div>
            <p className="text-muted small">
              Email the selected recipient list(s) when someone submits a CPD quiz. Recipients are
              managed in the Email Lists admin — choose which list(s) to notify here.
            </p>

            <div className="form-check form-switch mb-2">
              <input className="form-check-input" type="checkbox" id="cpdNotifyAttempt"
                checked={notifConfig.notify_on_attempt}
                onChange={e => setNotifConfig(prev => ({ ...prev, notify_on_attempt: e.target.checked }))} />
              <label className="form-check-label" htmlFor="cpdNotifyAttempt">
                Notify on every attempt (pass or fail)
              </label>
            </div>
            <div className="form-check form-switch mb-3">
              <input className="form-check-input" type="checkbox" id="cpdNotifyPass"
                checked={notifConfig.notify_on_pass}
                onChange={e => setNotifConfig(prev => ({ ...prev, notify_on_pass: e.target.checked }))} />
              <label className="form-check-label" htmlFor="cpdNotifyPass">
                Notify when a quiz is passed
              </label>
            </div>

            <label className="form-label fw-semibold mb-1">Recipient email lists</label>
            {emailLists.length === 0 ? (
              <p className="text-muted small">No email lists found. Create one in the Email Lists admin first.</p>
            ) : (
              <div className="mb-3">
                {emailLists.map(list => (
                  <div className="form-check" key={list.id}>
                    <input className="form-check-input" type="checkbox" id={`cpd-list-${list.id}`}
                      checked={notifConfig.email_list_ids.includes(list.id)}
                      onChange={() => toggleNotifList(list.id)} />
                    <label className="form-check-label" htmlFor={`cpd-list-${list.id}`}>
                      {list.name}{list.description ? ` — ${list.description}` : ''}
                    </label>
                  </div>
                ))}
              </div>
            )}

            {(notifConfig.notify_on_attempt || notifConfig.notify_on_pass) && notifConfig.email_list_ids.length === 0 && (
              <p className="text-warning small mb-2">Select at least one recipient list, or no emails will be sent.</p>
            )}

            <button className="btn btn-sm text-white" style={{ backgroundColor: '#072040' }}
              onClick={saveNotifConfig} disabled={notifSaving}>
              {notifSaving ? 'Saving…' : 'Save Notification Settings'}
            </button>
          </div>
        </div>
      )}

      {/* ─── Task Form ─── */}
      {showForm && (
        <div className="card mb-4">
          <div className="card-body">
            <h5>{editingTask ? 'Edit Task' : 'Create CPD Task'}</h5>
            <form onSubmit={handleSubmit}>
              <div className="row mb-3">
                <div className="col-md-3">
                  <label className="form-label">FAA Number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.faa_number}
                    onChange={e => setFormData({ ...formData, faa_number: e.target.value })}
                    required
                    disabled={!!editingTask}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Type</label>
                  <select
                    className="form-select"
                    value={formData.task_type}
                    onChange={e => setFormData({ ...formData, task_type: e.target.value })}
                    disabled={!!editingTask}
                  >
                    <option value="video">Video</option>
                    <option value="reading">Reading</option>
                    <option value="quiz">Quiz</option>
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="row mb-3">
                <div className="col-md-3">
                  <label className="form-label">CPD Hours</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0.5"
                    step="0.5"
                    value={formData.cpd_hours}
                    onChange={e => setFormData({ ...formData, cpd_hours: e.target.value })}
                    style={{ maxWidth: '120px' }}
                  />
                </div>
                <div className="col-md-5">
                  <label className="form-label">Knowledge Area</label>
                  <select
                    className="form-select"
                    value={formData.knowledge_area}
                    onChange={e => setFormData({ ...formData, knowledge_area: e.target.value })}
                  >
                    {KNOWLEDGE_AREAS.map(ka => (
                      <option key={ka.value} value={ka.value}>{ka.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {(formData.task_type === 'video' || formData.task_type === 'reading') && (
                <div className="mb-3">
                  <label className="form-label">Linked Resource</label>
                  <select
                    className="form-select"
                    value={formData.resource_item_id}
                    onChange={e => setFormData({ ...formData, resource_item_id: e.target.value })}
                  >
                    <option value="">— Select resource —</option>
                    {resourceItems.map(r => (
                      <option key={r.id} value={r.id}>{r.title} ({r.preview_type})</option>
                    ))}
                  </select>
                </div>
              )}

              {formData.task_type === 'quiz' && (
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="form-label">Pass Mark (%)</label>
                    <input
                      type="number"
                      className="form-control"
                      min={1}
                      max={100}
                      value={formData.pass_mark}
                      onChange={e => setFormData({ ...formData, pass_mark: e.target.value })}
                    />
                  </div>
                  <div className="col-md-9">
                    <label className="form-label">Case Study PDF</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".pdf"
                      onChange={e => setCaseStudyFile(e.target.files[0])}
                    />
                    {editingTask?.case_study_filename && !caseStudyFile && (
                      <small className="text-muted">Current: {editingTask.case_study_filename}</small>
                    )}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Certificate PDF</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf"
                  onChange={e => setCertificateFile(e.target.files[0])}
                />
                {editingTask?.certificate_filename && !certificateFile && (
                  <small className="text-muted">Current: {editingTask.certificate_filename}</small>
                )}
              </div>

              <button type="submit" className="btn text-white" style={{ backgroundColor: '#072040' }}>
                {editingTask ? 'Update Task' : 'Create Task'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ─── Task Table ─── */}
      {isLoading ? (
        <div className="text-center py-4"><div className="spinner-border" /></div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover">
            <thead style={{ backgroundColor: '#072040', color: '#fff' }}>
              <tr>
                <th>FAA Number</th>
                <th>Title</th>
                <th>Type</th>
                <th>CPD Hours</th>
                <th>Knowledge Area</th>
                <th>Pass Mark</th>
                <th>Questions</th>
                <th>Completions</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(task => (
                <tr key={task.id}>
                  <td><code>{task.faa_number}</code></td>
                  <td>{task.title}</td>
                  <td>
                    <span className={`badge ${task.task_type === 'video' ? 'bg-info' : task.task_type === 'reading' ? 'bg-secondary' : 'bg-warning text-dark'}`}>
                      {task.task_type === 'video' ? 'Video' : task.task_type === 'reading' ? 'Reading' : 'Quiz'}
                    </span>
                  </td>
                  <td>{task.cpd_hours ?? 1}</td>
                  <td>
                    <small>{KNOWLEDGE_AREAS.find(ka => ka.value === task.knowledge_area)?.label || task.knowledge_area}</small>
                  </td>
                  <td>{task.task_type === 'quiz' ? `${task.pass_mark || 80}%` : '—'}</td>
                  <td>
                    {task.task_type === 'quiz' ? (
                      <button className="btn btn-sm btn-outline-primary" onClick={() => openQuestions(task.id)}>
                        {task.questions_count} Qs
                      </button>
                    ) : '—'}
                  </td>
                  <td>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => openCompletions(task.id)}>
                      {task.completions_count || 0}
                    </button>
                  </td>
                  <td>{task.is_active ? '✓' : '✗'}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleEdit(task)}>Edit</button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(task.id)}>Delete</button>
                  </td>
                </tr>
              ))}
              {tasks.length === 0 && (
                <tr><td colSpan={10} className="text-center text-muted py-3">No CPD tasks yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Questions Modal ─── */}
      {showQuestions && (
        <div className="card mt-4 border-primary">
          <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#072040', color: '#fff' }}>
            <h5 className="mb-0">Questions — {tasks.find(t => t.id === showQuestions)?.title}</h5>
            <button className="btn btn-sm btn-light" onClick={() => { setShowQuestions(null); resetQuestionForm(); }}>Close</button>
          </div>
          <div className="card-body">
            {/* Question Form */}
            <form onSubmit={handleQuestionSubmit} className="mb-4 p-3 border rounded bg-light">
              <h6>{editingQuestion ? 'Edit Question' : 'Add Question'}</h6>
              <div className="mb-2">
                <label className="form-label">Question</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={questionForm.question_text}
                  onChange={e => setQuestionForm({ ...questionForm, question_text: e.target.value })}
                  required
                />
              </div>
              <div className="row mb-2">
                {questionForm.options.map((opt, i) => (
                  <div className="col-md-6 mb-2" key={i}>
                    <div className="input-group">
                      <div className="input-group-text">
                        <input
                          type="radio"
                          name="correct_option"
                          checked={questionForm.correct_option === i}
                          onChange={() => setQuestionForm({ ...questionForm, correct_option: i })}
                        />
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Option ${String.fromCharCode(65 + i)}`}
                        value={opt}
                        onChange={e => {
                          const opts = [...questionForm.options];
                          opts[i] = e.target.value;
                          setQuestionForm({ ...questionForm, options: opts });
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <small className="text-muted d-block mb-2">Select the radio button next to the correct answer</small>
              <button type="submit" className="btn btn-sm text-white" style={{ backgroundColor: '#072040' }}>
                {editingQuestion ? 'Update' : 'Add Question'}
              </button>
              {editingQuestion && (
                <button type="button" className="btn btn-sm btn-secondary ms-2" onClick={resetQuestionForm}>Cancel</button>
              )}
            </form>

            {/* Questions List */}
            {questions.length === 0 ? (
              <p className="text-muted">No questions yet. Add one above.</p>
            ) : (
              questions.map((q, idx) => (
                <div key={q.id} className="card mb-2">
                  <div className="card-body py-2">
                    <div className="d-flex justify-content-between">
                      <strong>Q{idx + 1}. {q.question_text}</strong>
                      <div>
                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleEditQuestion(q)}>Edit</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteQuestion(q.id)}>Delete</button>
                      </div>
                    </div>
                    <div className="mt-1">
                      {q.options.map((opt, oi) => (
                        <span key={oi} className={`badge me-1 ${oi === q.correct_option ? 'bg-success' : 'bg-secondary'}`}>
                          {String.fromCharCode(65 + oi)}: {opt}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ─── Completions Modal ─── */}
      {showCompletions && (
        <div className="card mt-4 border-secondary">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Completions — {tasks.find(t => t.id === showCompletions)?.title}</h5>
            <button className="btn btn-sm btn-secondary" onClick={() => setShowCompletions(null)}>Close</button>
          </div>
          <div className="card-body">
            {completions.length === 0 ? (
              <p className="text-muted">No completions yet.</p>
            ) : (
              <table className="table table-sm">
                <thead>
                  <tr><th>User</th><th>Email</th><th>Score</th><th>Passed</th><th>Completed</th></tr>
                </thead>
                <tbody>
                  {completions.map(c => (
                    <tr key={c.id}>
                      <td>{c.user_name || '—'}</td>
                      <td>{c.user_email}</td>
                      <td>{c.score != null ? `${c.score}%` : '—'}</td>
                      <td>{c.passed ? '✓' : '✗'}</td>
                      <td>{c.completed_at ? new Date(c.completed_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ─── Compliance Report ─── */}
      {showCompliance && (
        <div className="card mt-4 border-primary">
          <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#072040', color: '#fff' }}>
            <h5 className="mb-0">CPD Compliance Report</h5>
            <button className="btn btn-sm btn-light" onClick={() => setShowCompliance(false)}>Close</button>
          </div>
          <div className="card-body">
            <div className="d-flex align-items-center gap-3 mb-3">
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: '120px' }}
                value={complianceYear}
                onChange={e => {
                  const yr = parseInt(e.target.value);
                  setComplianceYear(yr);
                  fetchComplianceReport(yr);
                }}
              >
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button className="btn btn-sm btn-outline-success" onClick={handleExportCSV}>
                Export CSV
              </button>
            </div>

            {complianceLoading ? (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>
            ) : complianceData.length === 0 ? (
              <p className="text-muted">No adviser data found.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Total Hours</th>
                      <th>Technical</th>
                      <th>Client Care</th>
                      <th>Regulatory</th>
                      <th>Professionalism</th>
                      <th>Compliant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {complianceData.map(row => (
                      <tr key={row.user_id}>
                        <td>{row.name || '—'}</td>
                        <td>{row.email}</td>
                        <td><strong>{row.total_hours}</strong> / 40</td>
                        <td>{row.by_area?.technical || 0}</td>
                        <td>{row.by_area?.client_care || 0}</td>
                        <td>{row.by_area?.regulatory || 0}</td>
                        <td>{row.by_area?.professionalism || 0}</td>
                        <td>
                          <span className={`badge ${row.compliant ? 'bg-success' : 'bg-danger'}`}>
                            {row.compliant ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CpdEditor;
